import { JSONContent, Node } from '@tiptap/core';
import Placeholder from '@tiptap/extension-placeholder';
import { TextSelection } from '@tiptap/pm/state';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import camelCase from 'lodash/camelCase';
import React from 'react';

import {
  type MessageKey,
  type TranslationVars,
  useTranslation,
} from '@eluve/i18n';

import { SectionBubbleMenu } from './SectionBubbleMenu';
import { GroupTitle, TemplateGroup } from './TemplateGroupNode';
import { SectionTitle, TemplateSection } from './TemplateSectionNode';
import {
  docToDynamicArtifact,
  makeGroupNode,
  makeSectionNode,
} from './templateSectionsDoc';

const NEW_SECTION_LABEL = 'New Section';
const NEW_GROUP_LABEL = 'New Group';

/** Top-level document only holds sections and groups; no loose content. */
const SectionsDocument = Node.create({
  name: 'doc',
  topNode: true,
  content: '(templateSection | templateGroup)+',
});

const makeExtensions = (
  t: (key: MessageKey, vars?: TranslationVars) => string,
) => [
  SectionsDocument,
  StarterKit.configure({
    document: false,
    gapcursor: false,
    // The document is a flat list of sections/groups that the Add
    // section/group buttons manage; a trailing node is neither wanted nor
    // valid here. Worse, when the last node is an isolating group, the
    // trailing-node plugin coerces its append into an empty templateSection
    // — a ghost drag-handle-only card that also serializes as an empty
    // "Section N" block.
    trailingNode: false,
    // Bold indigo insertion line so the drop target is obvious while
    // dragging sections
    dropcursor: {
      color: 'var(--color-bg-indigo-strong-primary)',
      width: 3,
    },
    // Body content maps 1:1 to markdown for the LLM. Section titles are
    // already the top heading level, so bodies get two sub levels.
    heading: { levels: [2, 3] },
    bulletList: {
      HTMLAttributes: { class: 'list-disc list-outside pl-5' },
    },
    orderedList: {
      HTMLAttributes: { class: 'list-decimal list-outside pl-5' },
    },
  }),
  Placeholder.configure({
    placeholder: ({ node }) => {
      if (node.type.name === 'sectionTitle') {
        return t('outputTemplates.editor.sectionTitlePlaceholder');
      }
      if (node.type.name === 'groupTitle') {
        return t('outputTemplates.editor.groupNamePlaceholder');
      }
      return t('outputTemplates.editor.sectionBodyPlaceholder');
    },
    includeChildren: true,
  }),
  SectionTitle,
  TemplateSection,
  GroupTitle,
  TemplateGroup,
];

export interface TemplateSectionsEditorProps {
  /** Initial editor document; not reactive after mount */
  initialDoc: JSONContent;
  /** Called with the full document JSON on every edit */
  onDocChange: (doc: JSONContent) => void;
  /** Template name, included in the schema view */
  templateName?: string;
  /** Swaps the editor for the serialized template (admin debugging) */
  showSchema?: boolean;
  /** When false, the document is read-only (non-owner viewing) */
  editable?: boolean;
}

/** Imperative surface for the workspace's "Add section/group" buttons */
export type TemplateSectionsEditorHandle = {
  addSection: () => void;
  addGroup: () => void;
  /**
   * Scrolls the section (or group) whose derived key matches into view
   * and puts the caret in its title. Keys are camelCase(title), the
   * same derivation templateSectionsDoc uses, so preview block keys
   * map straight back to editor nodes.
   */
  scrollToSection: (key: string) => void;
};

/**
 * Section-based prompt editor: a single TipTap document that reads as
 * plain rich text from the outside, but whose top level is a flat list
 * of draggable template sections (title line + markdown body). Section
 * state (AI / required / delete) is edited via the bubble menu that
 * appears on the title. The document translates to/from
 * DynamicArtifactTemplate via templateSectionsDoc.ts.
 *
 * Eluve admins get a "Show schema" toggle that swaps the editor for
 * the serialized DynamicArtifactTemplate that would be persisted.
 */
export const TemplateSectionsEditor = React.forwardRef<
  TemplateSectionsEditorHandle,
  TemplateSectionsEditorProps
>(
  (
    {
      initialDoc,
      onDocChange,
      templateName,
      showSchema = false,
      editable = true,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    // Created once per editor instance: useEditor captures the extensions
    // on mount, so a language switch mid-edit keeps the mount-time
    // placeholders rather than rebuilding (and resetting) the editor.
    const [extensions] = React.useState(() => makeExtensions(t));
    const editor = useEditor({
      extensions,
      content: initialDoc,
      editable,
      editorProps: {
        attributes: {
          // px-0!/py-0! neutralize the global .ProseMirror px-6 py-2 from
          // eluve-editor's prosemirror.css for THIS instance only (sections
          // carry their own padding); the base style stays untouched.
          // That same stylesheet also sets `.ProseMirror * { margin: .5rem 0 }`,
          // which inflates the section/group title rows (so items-center drops
          // the title below its drag handle) and shifts the absolutely
          // positioned handles down. Zero the margin on the chrome — drag
          // handles and title rows — so each handle lines up with its title;
          // the title rows keep a small bottom gap before the body. Section
          // bodies keep the default spacing. The structural TipTap node-view
          // content wrappers also pick up that margin and push the titles
          // down, so zero them too (they should carry no vertical rhythm).
          // Tailwind preflight strips default heading styles, so size the
          // two body heading levels relative to the text-sm body copy.
          class:
            'focus:outline-none px-0! py-0! [&_[data-node-view-content]]:my-0! [&_[data-node-view-content-react]]:my-0! [&_[data-drag-handle]]:my-0! [&_.node-sectionTitle]:mt-0! [&_.node-sectionTitle]:mb-1! [&_.node-sectionTitle_*]:my-0! [&_[data-group-title]]:mt-0! [&_[data-group-title]]:mb-2! [&_h2]:mt-3 [&_h2]:mb-1 [&_h2]:text-base [&_h2]:font-bold [&_h3]:mt-2 [&_h3]:mb-0.5 [&_h3]:text-sm [&_h3]:font-semibold',
        },
      },
      onUpdate: ({ editor: currentEditor }) => {
        onDocChange(currentEditor.getJSON());
      },
    });

    React.useEffect(() => {
      editor?.setEditable(editable);
    }, [editor, editable]);

    /**
     * Append a top-level node (section or group) at the end of the document
     * and select its title placeholder so typing replaces it.
     */
    const appendNodeAtEnd = (nodeJson: JSONContent, labelLength: number) => {
      if (!editor) {
        return;
      }
      const { state } = editor;
      const insertPos = state.doc.content.size;
      const node = state.schema.nodeFromJSON(nodeJson);
      // +2: into the inserted node, then into its title's text
      const titleFrom = insertPos + 2;
      const tr = state.tr.insert(insertPos, node);
      tr.setSelection(
        TextSelection.create(tr.doc, titleFrom, titleFrom + labelLength),
      );
      // The add buttons sit above the sections, so bring the new node
      // (appended at the end) into view
      tr.scrollIntoView();
      editor.view.dispatch(tr);
      editor.view.focus();
    };

    const addSection = () => {
      appendNodeAtEnd(
        makeSectionNode({ label: NEW_SECTION_LABEL }),
        NEW_SECTION_LABEL.length,
      );
    };

    const addGroup = () => {
      appendNodeAtEnd(
        makeGroupNode({
          label: NEW_GROUP_LABEL,
          // Name + placeholder body so the starter section doesn't read
          // as an empty ghost (and survives ungrouping meaningfully)
          sections: [
            makeSectionNode({
              label: NEW_SECTION_LABEL,
              body: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      type: 'text',
                      text: '[describe the expected output for this section]',
                    },
                  ],
                },
              ],
            }),
          ],
        }),
        NEW_GROUP_LABEL.length,
      );
    };

    const scrollToSection = (key: string) => {
      if (!editor) {
        return;
      }
      let targetPos: number | null = null;
      editor.state.doc.descendants((node, pos) => {
        if (targetPos !== null) {
          return false;
        }
        if (
          node.type.name === 'templateSection' ||
          node.type.name === 'templateGroup'
        ) {
          const title = node.firstChild?.textContent.trim() ?? '';
          if (camelCase(title) === key) {
            targetPos = pos;
          }
          // Still descend into groups so their child sections can match
          return node.type.name === 'templateGroup' && targetPos === null;
        }
        return true;
      });
      if (targetPos === null) {
        return;
      }
      // Caret into the title (+2: into the node, into its title) so the
      // section highlights and its bubble menu opens; the smooth DOM
      // scroll below replaces TipTap's instant caret scroll
      editor.commands.focus(targetPos + 2, { scrollIntoView: false });
      const dom = editor.view.nodeDOM(targetPos);
      if (dom instanceof HTMLElement) {
        dom.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    React.useImperativeHandle(ref, () => ({
      addSection,
      addGroup,
      scrollToSection,
    }));

    const schemaJson =
      showSchema && editor
        ? JSON.stringify(
            docToDynamicArtifact({
              doc: editor.getJSON(),
              name: templateName || 'New Template',
            }),
            null,
            2,
          )
        : null;

    return (
      <div className="flex min-h-0 flex-1 flex-col gap-2">
        <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-border-primary bg-bg-primary p-2 text-sm">
          {schemaJson !== null && (
            <pre className="font-mono text-xs whitespace-pre-wrap text-text-secondary">
              {schemaJson}
            </pre>
          )}
          {/* The editor stays mounted and is hidden via CSS: unmounting
            EditorContent while React node views exist makes React and
            ProseMirror fight over the DOM (removeChild crashes) */}
          <div className={schemaJson !== null ? 'hidden' : undefined}>
            {editor && <SectionBubbleMenu editor={editor} />}
            <EditorContent editor={editor} />
          </div>
        </div>
      </div>
    );
  },
);

TemplateSectionsEditor.displayName = 'TemplateSectionsEditor';
